import { attr, FASTElement, Notifier, Observable, observable, Updates } from '@microsoft/fast-element';
import { dialogStyles, modalStyles } from './dialog.styles';
import { dialogTemplate, modalTemplate } from './dialog.template';
import { keyEscape, keyTab } from '@microsoft/fast-web-utilities';
import { getRootActiveElement } from '../../foundation/random';
import { isTabbable } from 'tabbable';
import { NatGenDesignSystem } from '../../natgen-design-system';

/**
 * @element natgen-dialog
 */
export class NatGenDialog extends FASTElement {
    content: HTMLElement;

    private readonly boundHide = () => this.hide();

    @observable
    public slottedTitleNodes: HTMLElement[];
    slottedTitleNodesChanged(oldValue: Node[], newValue: Node[]) {
        this.classList.toggle('has-title', newValue.length > 0);
    }

    @observable
    public slottedActionNodes: Node[];
    slottedActionNodesChanged(oldValue: Node[], newValue: Node[]) {
        this.classList.toggle('has-actions', newValue.length > 0);

        oldValue?.forEach(n => {
            n.removeEventListener('click', this.boundHide);
        });

        newValue?.forEach(n => {
            if(this.autoDismiss)
                n.addEventListener('click', this.boundHide);
        });
    }

    @attr({ mode: 'boolean', attribute: 'auto-dismiss' })
    autoDismiss: boolean;

    autoDismissChanged(oldValue: boolean, newValue: boolean): void {
        this.slottedActionNodes?.forEach(n => {
            if(newValue)
                n.addEventListener('click', this.boundHide);
            else
                n.removeEventListener('click', this.boundHide);
        });
    }

    /**
     * Indicates the element is modal. When modal, user mouse interaction will be limited to the contents of the element by a modal
     * overlay.  Clicks on the overlay will cause the dialog to emit a "dismiss" event.
     * @public
     * @defaultValue - false
     * @remarks
     * HTML Attribute: modal
     */
    @attr({ mode: "boolean" })
    public modal: boolean = false;

    /**
     * The hidden state of the element.
     *
     * @public
     * @defaultValue - false
     * @remarks
     * HTML Attribute: hidden
     */
    @attr({ mode: "boolean" })
    public hidden: boolean = false;

    /**
     * Indicates that the dialog should not trap focus.
     *
     * @public
     * @defaultValue - true
     * @remarks
     * HTML Attribute: no-focus-trap
     */
    @attr({ attribute: "no-focus-trap", mode: "boolean" })
    public noFocusTrap: boolean = false;
    private noFocusTrapChanged = (): void => {
        if (this.$fastController.isConnected) {
            this.updateTrapFocus();
        }
    };

    /**
     * The id of the element describing the dialog.
     * @public
     * @remarks
     * HTML Attribute: aria-describedby
     */
    @attr({ attribute: "aria-describedby" })
    public ariaDescribedby: string;

    /**
     * The id of the element labeling the dialog.
     * @public
     * @remarks
     * HTML Attribute: aria-labelledby
     */
    @attr({ attribute: "aria-labelledby" })
    public ariaLabelledby: string;

    /**
     * The label surfaced to assistive technologies.
     *
     * @public
     * @remarks
     * HTML Attribute: aria-label
     */
    @attr({ attribute: "aria-label" })
    public ariaLabel: string;

    /**
     * @internal
     */
    public dialog: HTMLDivElement;

    /**
     * @internal
     */
    private isTrappingFocus: boolean = false;

    /**
     * @internal
     */
    private notifier: Notifier;

    /**
     * @internal
     */
    public dismiss(): void {
        this.hidden = true;
        this.$emit("dismiss");
        // implement `<dialog>` interface
        this.$emit("cancel");
    }

    /**
     * The method to show the dialog.
     *
     * @public
     */
    public show(): void {
        this.hidden = false;
    }

    /**
     * The method to hide the dialog.
     *
     * @public
     */
    public hide(): void {
        this.hidden = true;
        // implement `<dialog>` interface
        this.$emit("close");
    }

    /**
     * @internal
     */
    public connectedCallback(): void {
        super.connectedCallback();

        document.addEventListener("keydown", this.handleDocumentKeydown);
        this.notifier = Observable.getNotifier(this);
        this.notifier.subscribe(this, "hidden");
        this.updateTrapFocus();
    }

    /**
     * @internal
     */
    public disconnectedCallback(): void {
        super.disconnectedCallback();

        // remove keydown event listener
        document.removeEventListener("keydown", this.handleDocumentKeydown);

        // if we are trapping focus remove the focusin listener
        this.updateTrapFocus(false);

        this.notifier.unsubscribe(this, "hidden");
    }

    /**
     * @internal
     */
    public handleChange(source: any, propertyName: string) {
        switch (propertyName) {
            case "hidden":
                this.updateTrapFocus();
                break;
            default:
                break;
        }
    }

    private handleDocumentKeydown = (e: KeyboardEvent): void => {
        if (!e.defaultPrevented && !this.hidden) {
            switch (e.key) {
                case keyEscape:
                    if (this.autoDismiss) {
                        this.dismiss();
                        e.preventDefault();
                    }
                    break;

                case keyTab:
                    this.handleTabKeyDown(e);
                    break;
            }
        }
    };

    private handleDocumentFocus = (e: Event): void => {
        if (!e.defaultPrevented && !(e.target instanceof NatGenDialog) && this.shouldForceFocus(e.target as HTMLElement)) {
            this.focusFirstElement();
            e.preventDefault();
        }
    };

    private handleTabKeyDown = (e: KeyboardEvent): void => {
        if (this.noFocusTrap || this.hidden) {
            return;
        }

        const bounds: (HTMLElement | SVGElement)[] = this.getTabQueueBounds();

        if (bounds.length === 0) {
            return;
        }

        if (bounds.length === 1) {
            // keep focus on single element
            bounds[0].focus();
            e.preventDefault();
            return;
        }

        if (e.shiftKey && e.target === bounds[0]) {
            bounds[bounds.length - 1].focus();
            e.preventDefault();
        } else if (!e.shiftKey && e.target === bounds[bounds.length - 1]) {
            bounds[0].focus();
            e.preventDefault();
        }

        return;
    };

    private getTabQueueBounds = (): (HTMLElement | SVGElement)[] => {
        const bounds: HTMLElement[] = [];

        return NatGenDialog.reduceTabbableItems(bounds, this);
    };

    /**
     * focus on first element of tab queue
     */
    private focusFirstElement = (): void => {
        const bounds: (HTMLElement | SVGElement)[] = this.getTabQueueBounds();

        if (bounds.length > 0) {
            bounds[0].focus();
        } else {
            if (this.dialog instanceof HTMLElement) {
                this.dialog.focus();
            }
        }
    };

    /**
     * we should only focus if focus has not already been brought to the dialog
     */
    private shouldForceFocus = (currentFocusElement: Element | null): boolean => {
        return this.isTrappingFocus && !this.contains(currentFocusElement);
    };

    /**
     * we should we be active trapping focus
     */
    private shouldTrapFocus = (): boolean => {
        return !this.noFocusTrap && !this.hidden;
    };

    /**
     *
     *
     * @internal
     */
    private updateTrapFocus = (shouldTrapFocusOverride?: boolean): void => {
        const shouldTrapFocus =
            shouldTrapFocusOverride === undefined
                ? this.shouldTrapFocus()
                : shouldTrapFocusOverride;

        if (shouldTrapFocus && !this.isTrappingFocus) {
            this.isTrappingFocus = true;
            // Add an event listener for focusin events if we are trapping focus
            document.addEventListener("focusin", this.handleDocumentFocus);
            Updates.enqueue(() => {
                if (this.shouldForceFocus(getRootActiveElement(this))) {
                    this.focusFirstElement();
                }
            });
        } else if (!shouldTrapFocus && this.isTrappingFocus) {
            this.isTrappingFocus = false;
            // remove event listener if we are not trapping focus
            document.removeEventListener("focusin", this.handleDocumentFocus);
        }
    };

    /**
     * Reduce a collection to only its focusable elements.
     *
     * @param elements - Collection of elements to reduce
     * @param element - The current element
     *
     * @internal
     */
    private static reduceTabbableItems(
        elements: HTMLElement[],
        element: FASTElement
    ): HTMLElement[] {
        if (element.getAttribute("tabindex") === "-1") {
            return elements;
        }

        if (
            isTabbable(element) ||
            (NatGenDialog.isFocusableFastElement(element) &&
                NatGenDialog.hasTabbableShadow(element))
        ) {
            elements.push(element);
            return elements;
        }

        if (element.childElementCount) {
            return elements.concat(
                Array.from(element.children).reduce(NatGenDialog.reduceTabbableItems, [])
            );
        }

        return elements;
    }

    /**
     * Test if element is focusable fast element
     *
     * @param element - The element to check
     *
     * @internal
     */
    private static isFocusableFastElement(element: FASTElement): boolean {
        return !!element.$fastController?.definition.shadowOptions?.delegatesFocus;
    }

    /**
     * Test if the element has a focusable shadow
     *
     * @param element - The element to check
     *
     * @internal
     */
    private static hasTabbableShadow(element: FASTElement) {
        return Array.from(element.shadowRoot?.querySelectorAll("*") ?? []).some(x => {
            return isTabbable(x);
        });
    }
}

export const natGenDialogDefinition = NatGenDialog.compose({
    name: `${NatGenDesignSystem.prefix}-dialog`,
    template: modalTemplate(),
    styles: modalStyles
});

// TODO: This should be the dialog component and the above should be renamed.
export const natGenInnerDialogDefinition = NatGenDialog.compose({
    name: `${NatGenDesignSystem.prefix}-inner-dialog`,
    template: dialogTemplate(),
    styles: dialogStyles
});

